/**
 * Plan feature access utilities.
 * Provides helpers for checking account access to gated plan features.
 */

import type { AugmentedAccount, PlanFeature } from './types';

/**
 * Generic helper to check if an account has unlimited access to a plan feature.
 * Premium accounts always have unlimited access.
 * For non-premium accounts:
 * - Feature must be enabled (present in gated_features)
 * - Feature limit must be false (unlimited) or 0 (unlimited per backend convention)
 *
 * @param account - The augmented account to check (must have planFeatures populated)
 * @param feature - The plan feature to check
 * @returns true if the account has unlimited access to the feature, false otherwise
 */
export function hasUnlimitedPlanFeatureAccess(account: AugmentedAccount, feature: PlanFeature): boolean {
  if (account.premium) return true;

  const planFeature = account.planFeatures[feature];
  // This should never happen if augmentAccount works correctly (it creates entries for all PlanFeature enum values)
  // But we include this check as defensive programming
  if (planFeature == null) {
    if (process.env.NODE_ENV === 'development') {
      // oxlint-disable-next-line no-console
      console.warn(
        `Plan feature ${feature} not found in account.planFeatures. This may indicate an issue with augmentAccount().`,
      );
    }
    return false;
  }

  // Feature must be enabled (present in backend gated_features)
  // If feature is not in gated_features, enabled is false and limit is false
  // We should default to limited access when feature is not enabled
  if (!planFeature.enabled) return false;

  // limit === false means unlimited
  // limit === 0 also means unlimited (backend convention)
  return planFeature.limit === false || planFeature.limit === 0;
}
